import { useEffect, useState } from 'react';
import fm from 'format-message';
import PropTypes from 'prop-types';
import useCategoryPageQuery from 'queries/useCategoryPageQuery';
import media from '@activebrands/core-web/config/media';
import { useProducts } from '@activebrands/core-web/contexts/ProductsContext';
import usePagination from '@activebrands/core-web/hooks/usePagination';
import useReInitWidgets from '@activebrands/core-web/libs/lipscore/hooks/useReInitWidgets';
import { transformButtons } from '@activebrands/core-web/libs/storyblok/utils/transform-buttons';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import NotFound from 'components/NotFound';
import Grid from 'components/product-listing/Grid';
import GridButton from 'components/product-listing/GridButton';
import SingleViewButton from 'components/product-listing/SingleViewButton';
import Paragraph from 'components/text/Paragraph';
import CategorySelectedFacets from 'templates/CategoryPage/CategorySelectedFacets';
import SearchSelectedFacets from 'templates/SearchPage/SearchView/SearchSelectedFacets';

let isClient = false;

const ListLayoutButtons = styled('div', {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',

    [media.min['desktop.sm']]: {
        display: 'none',
    },
});

const selectedFacets = {
    'category-facet-groups': CategorySelectedFacets,
    'search-facet-groups': SearchSelectedFacets,
};

const CategoryProductsListing = ({
    $style = {},
    gridContent = [],
    noProductsButtons = [],
    noProductsTitle = '',
    ssrPagination = false,
    showPageContent = false,
    filterOverlayId = 'category-facet-groups',
}) => {
    const [css] = useStyletron();
    const { products, cacheKey } = useProducts();
    const { list = [], page, pageCount } = usePagination(products, { cacheKey });
    const {
        noProductsTitle: categoryPageNoProductsTitle,
        filterSectionHeading,
        filterSectionParagraph,
        noProductsButton: categoryPageNoProductsButton,
    } = useCategoryPageQuery();

    useReInitWidgets(products);

    // Prevent SSR hydration bug for query params pages
    const [, triggerReRender] = useState(false);
    useEffect(() => {
        if (!isClient) {
            isClient = true;
            triggerReRender(true);
        }
    }, []);

    const SelectedFacets = selectedFacets[filterOverlayId];
    const totalNumberOfItems = products.length;

    return (
        <div className={css({ ...$style })} id="pagination-top">
            <div
                className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '12px var(--margin-content)',
                })}
            >
                <div
                    className={css({
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    })}
                >
                    <Paragraph
                        $style={{
                            display: ['initial', null, null, null, null, 'none'],
                            color: 'var(--color-text-subtle)',
                        }}
                        fontKeys="Secondary/12_130"
                    >
                        {totalNumberOfItems}&nbsp;
                        {fm('Products').toLowerCase()}
                    </Paragraph>
                    <ListLayoutButtons>
                        <Paragraph $style={{ color: 'var(--color-text-subtle)' }} fontKeys="Secondary/12_130">
                            {fm('View')}
                        </Paragraph>
                        <GridButton />
                        <SingleViewButton />
                    </ListLayoutButtons>
                </div>
                {SelectedFacets && <SelectedFacets $style={{ margin: '12px 0px' }} />}
            </div>
            {list.length ? (
                <Grid
                    filterOverlayId="category-facet-groups"
                    filterSectionHeading={filterSectionHeading}
                    filterSectionParagraph={filterSectionParagraph}
                    gridContent={gridContent}
                    items={list}
                    key={isClient}
                    page={page}
                    pageCount={pageCount}
                    paginationType={ssrPagination ? 'url' : 'query'}
                    totalNumberOfItems={products.length}
                    type="products"
                    showPageContent={showPageContent}
                />
            ) : (
                <div className={css({ margin: '64px auto' })}>
                    <NotFound
                        heading={noProductsTitle || categoryPageNoProductsTitle}
                        links={transformButtons(
                            noProductsButtons.length ? noProductsButtons : categoryPageNoProductsButton
                        )}
                    />
                </div>
            )}
        </div>
    );
};

CategoryProductsListing.propTypes = {
    $style: PropTypes.object,
    gridContent: PropTypes.array,
    noProductsButtons: PropTypes.array,
    noProductsTitle: PropTypes.string,
    showPageContent: PropTypes.bool,
    ssrPagination: PropTypes.bool,
};

export default CategoryProductsListing;
